import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import type { Address } from 'viem';

import { useSimulateBalanceMutations } from 'hooks/useSimulateBalanceMutations';
import type { Asset, AssetBalanceMutation, Pool } from 'types';

import { useGetWrapperExchangeRate } from '../shared/useGetWrapperExchangeRate';
import type { FormValues } from './useForm';

interface UseSupplySimulationInput {
  asset: Asset;
  borrowTargetAsset?: Asset;
  bundleBorrowAmountTokens: string;
  bundleBorrowLegActive: boolean;
  formValues: FormValues;
  isUsingBittensorNativeSupplySource: boolean;
  pool: Pool;
  toTokenAmountTokens: BigNumber;
  userSlippageTolerancePercentage: number;
}

export const useSupplySimulation = ({
  asset,
  borrowTargetAsset,
  bundleBorrowAmountTokens,
  bundleBorrowLegActive,
  formValues,
  isUsingBittensorNativeSupplySource,
  pool,
  toTokenAmountTokens,
  userSlippageTolerancePercentage,
}: UseSupplySimulationInput) => {
  const { data: supplyWrapperExchangeRateBigInt } = useGetWrapperExchangeRate(
    { wrapperAddress: asset.vToken.underlyingToken.address as Address },
    {
      enabled: isUsingBittensorNativeSupplySource && toTokenAmountTokens.isGreaterThan(0),
    },
  );

  // Native-source supplies are wrapped before collateral is credited. The
  // slippage haircut prices the preview against the worst-case wrapper output,
  // so SafeMax cannot size a borrow against collateral that may not land.
  const projectedSupplyCollateralTokens = useMemo(() => {
    if (!isUsingBittensorNativeSupplySource) return toTokenAmountTokens;
    if (!supplyWrapperExchangeRateBigInt || supplyWrapperExchangeRateBigInt === 0n) {
      return toTokenAmountTokens;
    }
    const nativeDecimalBase = new BigNumber(10).pow(formValues.fromToken.decimals);
    const baseWrapperAmount = toTokenAmountTokens
      .multipliedBy(nativeDecimalBase)
      .dividedBy(new BigNumber(supplyWrapperExchangeRateBigInt.toString()));
    const slippageHaircut = new BigNumber(1).minus(
      new BigNumber(userSlippageTolerancePercentage).div(100),
    );
    if (slippageHaircut.isLessThanOrEqualTo(0)) return baseWrapperAmount;
    return baseWrapperAmount.multipliedBy(slippageHaircut);
  }, [
    isUsingBittensorNativeSupplySource,
    toTokenAmountTokens,
    supplyWrapperExchangeRateBigInt,
    formValues.fromToken.decimals,
    userSlippageTolerancePercentage,
  ]);

  const supplyOnlyBalanceMutations: AssetBalanceMutation[] = useMemo(
    () => [
      {
        type: 'asset',
        vTokenAddress: asset.vToken.address,
        action: 'supply',
        amountTokens: projectedSupplyCollateralTokens,
      },
    ],
    [asset.vToken.address, projectedSupplyCollateralTokens],
  );

  const balanceMutations: AssetBalanceMutation[] = useMemo(() => {
    if (!bundleBorrowLegActive || !borrowTargetAsset) return supplyOnlyBalanceMutations;
    return [
      ...supplyOnlyBalanceMutations,
      {
        type: 'asset',
        vTokenAddress: borrowTargetAsset.vToken.address,
        action: 'borrow',
        amountTokens: new BigNumber(bundleBorrowAmountTokens),
      },
    ];
  }, [
    bundleBorrowLegActive,
    supplyOnlyBalanceMutations,
    borrowTargetAsset,
    bundleBorrowAmountTokens,
  ]);

  const { data: getSimulatedPoolData } = useSimulateBalanceMutations({
    pool,
    balanceMutations,
  });
  const { data: getSupplyOnlySimulatedPoolData } = useSimulateBalanceMutations({
    pool,
    balanceMutations: supplyOnlyBalanceMutations,
  });

  return {
    balanceMutations,
    simulatedPool: getSimulatedPoolData?.pool,
    supplyOnlySimulatedPool: getSupplyOnlySimulatedPoolData?.pool,
  };
};
